import React, {
  Dispatch,
  FC,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react'

import {
  InvestmentProduct,
  useInvestmentAccountsQueryQuery,
} from 'generated/types'

type CashOwedState = {
  isInvestCashOwed: boolean
  setIsInvestCashOwed: Dispatch<SetStateAction<boolean>>
  isEarlyCashOwed: boolean
  setIsEarlyCashOwed: Dispatch<SetStateAction<boolean>>
  isLaterCashOwed: boolean
  setIsLaterCashOwed: Dispatch<SetStateAction<boolean>>
  isCashOwedModalOpen: boolean
  setIsCashOwedModalOpen: Dispatch<SetStateAction<boolean>>
  setBeneficiaryId: Dispatch<SetStateAction<string>>
  investCashOwed?: number
  earlyCashOwed?: number
  laterCashOwed?: number
}

const CashOwedContext = createContext({} as CashOwedState)

export const CashOwedProvider: FC = ({ children }) => {
  const [isInvestCashOwed, setIsInvestCashOwed] = useState(false)
  const [isEarlyCashOwed, setIsEarlyCashOwed] = useState(false)
  const [isLaterCashOwed, setIsLaterCashOwed] = useState(false)
  const [isCashOwedModalOpen, setIsCashOwedModalOpen] = useState(false)
  const [beneficiaryId, setBeneficiaryId] = useState('')

  const { data: coreInvestmentAccounts, loading: isLoadingInvestmentAccounts } =
    useInvestmentAccountsQueryQuery({
      variables: {
        product: InvestmentProduct.Invest,
      },
    })

  const coreInvestmentAccount =
    coreInvestmentAccounts?.investmentAccountsByUserId.filter(
      (investmentAccount) => investmentAccount.status === 'OPEN',
    )
  const investCashOwed = coreInvestmentAccount?.[0]?.cashOwed?.value

  const {
    data: earlyInvestmentAccounts,
    loading: isLoadingEarlyInvestmentAccounts,
  } = useInvestmentAccountsQueryQuery({
    variables: {
      product: InvestmentProduct.Early,
    },
  })

  const earlyInvestmentAccount =
    earlyInvestmentAccounts?.investmentAccountsByUserId.filter(
      (investmentAccount) => investmentAccount.beneficiaryId === beneficiaryId,
    )
  const earlyCashOwed = earlyInvestmentAccount?.[0]?.cashOwed?.value

  const {
    data: laterInvestmentAccounts,
    loading: isLoadingLaterInvestmentAccounts,
  } = useInvestmentAccountsQueryQuery({
    variables: {
      product: InvestmentProduct.Later,
    },
  })

  //TODO: When Acorns supports multiple Later accounts, we should modify this filter to support the specific later account
  const laterInvestmentAccount =
    laterInvestmentAccounts?.investmentAccountsByUserId.filter(
      (investmentAccount) => investmentAccount.status === 'OPEN',
    )
  const laterCashOwed = laterInvestmentAccount?.[0]?.cashOwed?.value

  useEffect(() => {
    if (!isLoadingInvestmentAccounts && investCashOwed && investCashOwed > 0) {
      setIsInvestCashOwed(true)
    }
    if (
      !isLoadingEarlyInvestmentAccounts &&
      earlyCashOwed &&
      earlyCashOwed > 0
    ) {
      setIsEarlyCashOwed(true)
    }
    if (
      !isLoadingLaterInvestmentAccounts &&
      laterCashOwed &&
      laterCashOwed > 0
    ) {
      setIsLaterCashOwed(true)
    }
  }, [
    earlyCashOwed,
    investCashOwed,
    isLoadingEarlyInvestmentAccounts,
    isLoadingInvestmentAccounts,
    isLoadingLaterInvestmentAccounts,
    laterCashOwed,
  ])

  return (
    <CashOwedContext.Provider
      value={{
        isInvestCashOwed,
        isEarlyCashOwed,
        isLaterCashOwed,
        setIsInvestCashOwed,
        setIsEarlyCashOwed,
        setIsLaterCashOwed,
        isCashOwedModalOpen,
        setIsCashOwedModalOpen,
        setBeneficiaryId,
        investCashOwed,
        earlyCashOwed,
        laterCashOwed,
      }}
    >
      {children}
    </CashOwedContext.Provider>
  )
}

export const useCashOwedContext = () => useContext(CashOwedContext)
